import type { DefLookup } from "./catalog";
import type { SimulateContext } from "./cable-current";
import { getEffectiveStateOption } from "./simulate/states";
import { buildGraph, findTerminals, isBatteryPositive, splitV, v, type Graph, type Vertex } from "./diagnostics/graph";
import { terminalPolarity } from "./terminal-snap";
import type { ComponentDefinition, System } from "./types";

/**
 * Batteries wired into one bank: cells stacked in series (a + landing on the
 * next cell's −, directly, through a junction or across a bus), strings tied
 * in parallel (+ to + and − to −), or both. Found from the drawing, not from
 * `Cable.seriesLink` — the flag confirms an otherwise ambiguous link; complete
 * series circuits can also be inferred by polarity diagnostics. A bank drawn
 * through junctions or as a 2P8S cell block carries none, which is how a
 * 24 V house side computed at 12.8 V and
 * sixteen 3.2 V cells each raised their own fuse-distance warning.
 */
export type BatteryStack = {
  id: string;
  batteryNodeIds: string[];
  /** Series levels bottom → top; each level is the batteries in parallel there. */
  levels: string[][];
  /** Σ over levels of the level's nominal voltage. */
  nominalV: number;
  /** The string's capacity: the smallest level's Σ Ah. */
  capacityAh: number;
  /** Set when every member agrees. */
  chemistry?: ComponentDefinition["chemistry"];
  /** Battery + terminals on the top level — where the bank's feeders leave. */
  topPositives: Vertex[];
  /** Battery − terminals on the bottom level. */
  bottomNegatives: Vertex[];
};

export type BatteryStacks = {
  stacks: BatteryStack[];
  stackOfBattery: ReadonlyMap<string, BatteryStack>;
};

/** Fraction of a bus's current through each physical battery. Parallel paths
 * share by capacity; every series level carries its path's current. Summation
 * keeps a cell shared by multiple paths from disappearing or being overwritten.
 * Unknown capacities use equal sharing as a planning estimate, never zero. */
export function batteryCurrentShares(
  stacks: readonly BatteryStack[],
  capacity: (batteryId: string) => number,
): Map<string, number> {
  const fractions = new Map<string, number>();
  const totalCapacity = stacks.reduce((sum, stack) => sum + stack.capacityAh, 0);
  const knownPaths = stacks.every(stack => stack.capacityAh > 0);
  for (const stack of stacks) {
    const share = knownPaths ? stack.capacityAh / totalCapacity : 1 / stacks.length;
    for (const level of stack.levels) {
      const levelCapacity = level.reduce((sum, id) => sum + capacity(id), 0);
      const knownLevel = level.every(id => capacity(id) > 0);
      for (const id of level) {
        const fraction = share * (knownLevel ? capacity(id) / levelCapacity : 1 / level.length);
        fractions.set(id, (fractions.get(id) ?? 0) + fraction);
      }
    }
  }
  return fractions;
}

const EMPTY: BatteryStacks = { stacks: [], stackOfBattery: new Map() };

const cache = new WeakMap<System, WeakMap<DefLookup, BatteryStacks>>();

/** Stacks of the system, one per bank; a lone battery is a one-level stack.
 *  Keyed on `System` identity — the store hands out a fresh object per mutation. */
export function computeBatteryStacks(system: System, defLookup: DefLookup): BatteryStacks {
  let byDefinition = cache.get(system);
  if (!byDefinition) {
    byDefinition = new WeakMap();
    cache.set(system, byDefinition);
  }
  const cached = byDefinition.get(defLookup);
  if (cached) return cached;
  const result = computeBatteryStacksUncached(system, defLookup);
  byDefinition.set(defLookup, result);
  return result;
}

/** True for a member of a multi-level (series) stack. */
export function isSeriesStacked(stacks: BatteryStacks, batteryNodeId: string): boolean {
  return (stacks.stackOfBattery.get(batteryNodeId)?.levels.length ?? 0) > 1;
}

/**
 * The electrical node a terminal sits on: everything reachable over cables and
 * the conductors inside distribution parts (a bus bar's studs, a switch's
 * poles, a shunt's two posts, a fuse — a fuse is a conductor for potential).
 * A battery, load or charger bridges nothing, so a walk never passes
 * *through* a part that establishes or drops a voltage. A monitor's non-power
 * terminals are taps (a BMS's balance "+", a BMV's sense): a custom 8S BMS
 * drawn with one "+" pin and eight leads would otherwise short every level of
 * the block into one node.
 */
function regionIndex(graph: Graph, taps: ReadonlySet<Vertex>): (vertex: Vertex) => number {
  const regionOf = new Map<Vertex, number>();
  let next = 0;
  return (start: Vertex): number => {
    const known = regionOf.get(start);
    if (known != null) return known;
    const id = next++;
    const queue: Vertex[] = [start];
    regionOf.set(start, id);
    while (queue.length) {
      const cur = queue.shift()!;
      for (const { vertex, via } of graph.adj.get(cur) ?? []) {
        if (via.kind === "cable" && taps.has(vertex)) continue;
        if (regionOf.has(vertex)) continue;
        regionOf.set(vertex, id);
        queue.push(vertex);
      }
    }
    return id;
  };
}

function monitorTapVertices(system: System, defLookup: DefLookup): Set<Vertex> {
  const taps = new Set<Vertex>();
  for (const n of system.nodes) {
    if (n.kind !== "component") continue;
    const def = defLookup(n.definitionId, n.customDefinition);
    if (def?.category !== "monitor") continue;
    for (const t of def.terminals) {
      if (t.polarity === "negative" || t.polarity === "ground") continue;
      taps.add(v(n.id, t.id));
    }
  }
  return taps;
}

/** Physical battery rails, with monitor taps excluded consistently from source
 * extraction, inventory and voltage diagnostics. The returned IDs are local to
 * this index and carry no voltage or source-completeness claim. */
export function batteryConductorRegionIndex(system: System, defLookup: DefLookup, graph: Graph): (vertex: Vertex) => number {
  return regionIndex(graph, monitorTapVertices(system, defLookup));
}

type Member = {
  nodeId: string;
  def: ComponentDefinition;
  posVerts: Vertex[];
  negVerts: Vertex[];
  posRegion: number;
  negRegion: number;
};

/** Live topology, including open switches; never cached as the edit-time bank. */
export function computeBatteryStacksForGraph(system: System, defLookup: DefLookup, graph: Graph): BatteryStacks {
  return computeBatteryStacksUncached(system, defLookup, graph);
}

/** Every physically connected, contiguous battery path between two rails.
 * Unlike an inventory bank's aggregated levels, a path never joins intermediate
 * taps on separate parallel strings. Consumers may sum the contributions from
 * several paths sharing the same endpoint rails. */
export function computeBatterySourceRanges(system: System, defLookup: DefLookup, graph: Graph, simulate?: SimulateContext): BatteryStack[] {
  return analyzeBatterySourceRanges(system, defLookup, graph, simulate).ranges;
}

export type BatterySourceRangeAnalysis =
  | { complete: true; ranges: BatteryStack[] }
  | { complete: false; ranges: []; reason: "source-range-limit" };

// Computational budgets, not electrical limits. A pathological graph can
// describe exponentially many contiguous source paths; never freeze the editor
// or publish its first few paths as a complete source model.
const MAX_SOURCE_RANGES = 1024;
const MAX_SOURCE_LEVELS = 128;

/** Source extraction with an explicit unsupported result. The compatibility
 * array API above fails closed; topology/current/protection consumers that
 * need to distinguish absent from unresolved sources use this result. */
export function analyzeBatterySourceRanges(system: System, defLookup: DefLookup, graph: Graph, simulate?: SimulateContext): BatterySourceRangeAnalysis {
  const region = batteryConductorRegionIndex(system, defLookup, graph);
  const groups = new Map<string, Member[]>();
  for (const node of system.nodes) {
    if (node.kind !== "component") continue;
    const def = defLookup(node.definitionId, node.customDefinition);
    if (def?.category !== "battery") continue;
    const state = getEffectiveStateOption(def, node.id, simulate);
    if (state?.ampOverride === 0 || state?.ampMultiplier === 0) continue;
    const posVerts = def.terminals.filter(t => t.polarity === "positive").map(t => v(node.id, t.id));
    // A separate case/chassis bond must not replace a declared DC negative.
    const hasNegative = def.terminals.some(t => t.polarity === "negative");
    const negVerts = def.terminals.filter(t => t.polarity === "negative" || (!hasNegative && t.polarity === "ground")).map(t => v(node.id, t.id));
    if (posVerts.length === 0 || negVerts.length === 0) continue;
    const posRegion = region(posVerts[0]), negRegion = region(negVerts[0]);
    if (posRegion === negRegion) continue;
    const key = `${posRegion}|${negRegion}`;
    const members = groups.get(key) ?? [];
    members.push({ nodeId: node.id, def, posVerts, negVerts, posRegion, negRegion });
    groups.set(key, members);
  }
  const above = new Map<number, Member[][]>();
  for (const group of groups.values()) {
    const list = above.get(group[0].negRegion) ?? [];
    list.push(group);
    above.set(group[0].negRegion, list);
  }
  const ranges: BatteryStack[] = [];
  let limited = false;
  const visit = (path: Member[][], seenRails: ReadonlySet<number>) => {
    if (limited) return;
    if (ranges.length >= MAX_SOURCE_RANGES || path.length > MAX_SOURCE_LEVELS) { limited = true; return; }
    const top = path[path.length - 1];
    const chemistry = new Set(path.flatMap(level => level.map(member => member.def.chemistry)));
    ranges.push({
      id: `source-range-${ranges.length}`,
      batteryNodeIds: path.flatMap(level => level.map(member => member.nodeId)),
      levels: path.map(level => level.map(member => member.nodeId)),
      nominalV: Math.round(path.reduce((sum, level) => sum + Math.max(...level.map(member => member.def.voltage ?? 12)), 0) * 1000) / 1000,
      capacityAh: Math.min(...path.map(level => level.reduce((sum, member) => sum + (member.def.capacityAh ?? 0), 0))),
      chemistry: chemistry.size === 1 ? [...chemistry][0] : undefined,
      bottomNegatives: path[0].flatMap(member => member.negVerts),
      topPositives: top.flatMap(member => member.posVerts),
    });
    for (const next of above.get(top[0].posRegion) ?? []) {
      if (seenRails.has(next[0].posRegion)) continue;
      visit([...path, next], new Set([...seenRails, next[0].posRegion]));
    }
  };
  for (const group of groups.values()) {
    visit([group], new Set([group[0].negRegion, group[0].posRegion]));
    if (limited) break;
  }
  return limited ? { complete: false, ranges: [], reason: "source-range-limit" } : { complete: true, ranges };
}

function computeBatteryStacksUncached(system: System, defLookup: DefLookup, graph = buildGraph(system, defLookup)): BatteryStacks {
  const members: Member[] = [];
  const region = batteryConductorRegionIndex(system, defLookup, graph);
  for (const n of system.nodes) {
    if (n.kind !== "component") continue;
    const def = defLookup(n.definitionId, n.customDefinition);
    if (!def || def.category !== "battery") continue;
    const posVerts = def.terminals.filter((t) => t.polarity === "positive").map((t) => v(n.id, t.id));
    const negVerts = def.terminals
      .filter((t) => t.polarity === "negative" || t.polarity === "ground")
      .map((t) => v(n.id, t.id));
    if (posVerts.length === 0 || negVerts.length === 0) continue;
    members.push({
      nodeId: n.id,
      def,
      posVerts,
      negVerts,
      posRegion: region(posVerts[0]),
      negRegion: region(negVerts[0]),
    });
  }
  if (members.length === 0) return EMPTY;

  // Parallel-equivalent batteries share both nodes and collapse into one cell
  // group; series edges run from a group to the group whose − sits on its +.
  const groupKey = (m: Member) => `${m.posRegion}|${m.negRegion}`;
  const groups = new Map<string, Member[]>();
  for (const m of members) {
    // A battery whose + and − share a node is shorted; it stacks with nothing.
    if (m.posRegion === m.negRegion) {
      groups.set(`solo|${m.nodeId}`, [m]);
      continue;
    }
    const list = groups.get(groupKey(m));
    if (list) list.push(m);
    else groups.set(groupKey(m), [m]);
  }
  const groupIds = [...groups.keys()];
  const byPosRegion = new Map<number, string[]>();
  const byNegRegion = new Map<number, string[]>();
  for (const [gid, ms] of groups) {
    if (gid.startsWith("solo|")) continue;
    const { posRegion, negRegion } = ms[0];
    (byPosRegion.get(posRegion) ?? byPosRegion.set(posRegion, []).get(posRegion)!).push(gid);
    (byNegRegion.get(negRegion) ?? byNegRegion.set(negRegion, []).get(negRegion)!).push(gid);
  }
  // above[g] = groups whose − is on g's +; below[g] = groups whose + is on g's −.
  const above = new Map<string, string[]>();
  const below = new Map<string, string[]>();
  for (const gid of groupIds) {
    if (gid.startsWith("solo|")) {
      above.set(gid, []);
      below.set(gid, []);
      continue;
    }
    const { posRegion, negRegion } = groups.get(gid)![0];
    above.set(gid, (byNegRegion.get(posRegion) ?? []).filter((x) => x !== gid));
    below.set(gid, (byPosRegion.get(negRegion) ?? []).filter((x) => x !== gid));
  }

  // Parallel strings must share both ends. A shared positive or negative alone
  // cannot make another battery's capacity available to the same load.
  const bottomRegions = (gid: string, seen = new Set<string>()): Set<number> => {
    if (seen.has(gid)) return new Set();
    const nextSeen = new Set([...seen, gid]);
    const lower = below.get(gid) ?? [];
    if (lower.length === 0) return new Set([groups.get(gid)![0].negRegion]);
    return new Set(lower.flatMap(id => [...bottomRegions(id, nextSeen)]));
  };
  // Pool only matching ordered cell voltages. Equal-voltage independent
  // strings can have different depths (12 vs 6+6) or profiles (4+8 vs 6+6).
  // Maxima across unlike levels would invent voltage and lose capacity.
  const profiles = new Map<string, number[] | undefined>();
  const voltageProfile = (gid: string, seen = new Set<string>()): number[] | undefined => {
    if (profiles.has(gid)) return profiles.get(gid);
    if (seen.has(gid)) return undefined;
    const nextSeen = new Set([...seen, gid]);
    const lower = (below.get(gid) ?? []).map(id => voltageProfile(id, nextSeen));
    const volts = groups.get(gid)!.map(member => member.def.voltage);
    let profile: number[] | undefined;
    if (volts.every(voltage => voltage != null && Number.isFinite(voltage) && voltage > 0)
      && lower.every(value => value != null && JSON.stringify(value) === JSON.stringify(lower[0]))) {
      profile = [...(lower[0] ?? []), Math.max(...volts as number[])];
    }
    profiles.set(gid, profile);
    return profile;
  };
  const parallelAtTop = (gid: string): string[] => {
    if (gid.startsWith("solo|")) return [];
    const { posRegion } = groups.get(gid)![0];
    const returns = bottomRegions(gid);
    const profile = voltageProfile(gid);
    return (byPosRegion.get(posRegion) ?? []).filter(x => x !== gid && profile != null
      && JSON.stringify(voltageProfile(x)) === JSON.stringify(profile) && [...bottomRegions(x)].some(r => returns.has(r)));
  };
  const stackOfGroup = new Map<string, number>();
  const componentGroups: string[][] = [];
  for (const gid of groupIds) {
    if (stackOfGroup.has(gid)) continue;
    const comp: string[] = [];
    const stackIdx = componentGroups.length;
    const queue = [gid];
    stackOfGroup.set(gid, stackIdx);
    while (queue.length) {
      const cur = queue.shift()!;
      comp.push(cur);
      for (const nb of [
        ...(above.get(cur) ?? []),
        ...(below.get(cur) ?? []),
        ...parallelAtTop(cur),
      ]) {
        if (stackOfGroup.has(nb)) continue;
        stackOfGroup.set(nb, stackIdx);
        queue.push(nb);
      }
    }
    componentGroups.push(comp);
  }

  const stacks: BatteryStack[] = [];
  const stackOfBattery = new Map<string, BatteryStack>();
  componentGroups.forEach((comp, idx) => {
    // Level = longest chain of series edges below the group. A ring (a group
    // both above and below another) has no bottom; every group then sits at
    // level 0 and the "stack" reports one level, which is the honest answer
    // for a drawing the model cannot order.
    const level = new Map<string, number>();
    const visiting = new Set<string>();
    let ring = false;
    const levelOf = (gid: string): number => {
      const known = level.get(gid);
      if (known != null) return known;
      if (visiting.has(gid)) {
        ring = true;
        return 0;
      }
      visiting.add(gid);
      let l = 0;
      for (const b of below.get(gid) ?? []) l = Math.max(l, levelOf(b) + 1);
      visiting.delete(gid);
      level.set(gid, l);
      return l;
    };
    for (const gid of comp) levelOf(gid);
    if (ring) for (const gid of comp) level.set(gid, 0);

    const levelCount = Math.max(...comp.map((g) => level.get(g) ?? 0)) + 1;
    const levels: string[][] = Array.from({ length: levelCount }, () => []);
    const levelMembers: Member[][] = Array.from({ length: levelCount }, () => []);
    for (const gid of comp) {
      const l = level.get(gid) ?? 0;
      for (const m of groups.get(gid)!) {
        levels[l].push(m.nodeId);
        levelMembers[l].push(m);
      }
    }
    let nominalV = 0;
    let capacityAh = Number.POSITIVE_INFINITY;
    for (const ms of levelMembers) {
      nominalV += Math.max(...ms.map((m) => m.def.voltage ?? 0));
      capacityAh = Math.min(capacityAh, ms.reduce((s, m) => s + (m.def.capacityAh ?? 0), 0));
    }
    const chemistries = new Set(comp.flatMap((g) => groups.get(g)!.map((m) => m.def.chemistry)));
    const stack: BatteryStack = {
      id: `stack-${idx}`,
      batteryNodeIds: levels.flat(),
      levels,
      // 8 × 3.2 is 25.599999999999998 in floating point.
      nominalV: Math.round(nominalV * 1000) / 1000,
      capacityAh: Number.isFinite(capacityAh) ? capacityAh : 0,
      chemistry: chemistries.size === 1 ? [...chemistries][0] : undefined,
      topPositives: levelMembers[levelCount - 1].flatMap((m) => m.posVerts),
      bottomNegatives: levelMembers[0].flatMap((m) => m.negVerts),
    };
    stacks.push(stack);
    for (const id of stack.batteryNodeIds) stackOfBattery.set(id, stack);
  });

  return { stacks, stackOfBattery };
}

/** Battery + terminals that are the top of their stack — the posts a bank's
 *  feeders leave from. An inner series post is not one. */
export function bankPositiveVertices(stacks: BatteryStacks): Set<Vertex> {
  const out = new Set<Vertex>();
  for (const s of stacks.stacks) for (const vert of s.topPositives) out.add(vert);
  return out;
}

/** Node id of the vertex — for callers that hold a `Vertex` from a stack. */
export const stackVertexNodeId = (vertex: Vertex): string => splitV(vertex).nodeId;

/** A current/voltage-drop path through series batteries, ending at the bottom
 * battery positive. Never use this graph for potential or protection checks:
 * its battery bridges conserve current without declaring the posts equipotential. */
export function batterySupplyGraph(
  system: System,
  defLookup: DefLookup,
  graph: Graph,
): { graph: Graph; positives: Vertex[] } {
  // Current entering the top of a series string also crosses every jumper.
  // Continue the positive demand walk through the upper batteries to the
  // bottom battery's positive post. These synthetic edges belong ONLY to the
  // current walk: batteries still separate potentials in the electrical graph.
  // A midpoint load stops below its tap; a junction, switch or parallel string
  // uses the same flow splitting and simulate-state graph as ordinary demand.
  const upperBatteries = new Set(
    computeBatteryStacks(system, defLookup).stacks.flatMap((s) => s.levels.slice(1).flat()),
  );
  let positiveGraph = graph;
  if (upperBatteries.size > 0) {
    positiveGraph = { ...graph, adj: new Map(graph.adj) };
    for (const node of system.nodes) {
      if (node.kind !== "component" || !upperBatteries.has(node.id)) continue;
      const def = defLookup(node.definitionId, node.customDefinition);
      if (!def) continue;
      for (const pos of def.terminals.filter((t) => t.polarity === "positive")) {
        for (const neg of def.terminals.filter((t) => terminalPolarity(t) === "negative")) {
          const a = v(node.id, pos.id), b = v(node.id, neg.id);
          const via = { kind: "intra" as const, link: {
            nodeId: node.id, from: pos.id, to: neg.id, kind: "passthrough" as const,
          } };
          positiveGraph.adj.set(a, [...(positiveGraph.adj.get(a) ?? []), { vertex: b, via }]);
          positiveGraph.adj.set(b, [...(positiveGraph.adj.get(b) ?? []), { vertex: a, via }]);
        }
      }
    }
  }
  const positiveSeeds = findTerminals(system, defLookup, isBatteryPositive).filter((vertex) => !upperBatteries.has(splitV(vertex).nodeId));
  return { graph: positiveGraph, positives: positiveSeeds };
}
